import * as React from "react";
import { useState, useCallback } from "react";
import URI from "urijs";
import { useTranslation } from "react-i18next";
import { Reports } from "@graylog/enterprise-api";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import { MenuItem, DeleteMenuItem } from "components/bootstrap";
import ButtonToolbar from "components/bootstrap/ButtonToolbar";
import useSelectedEntities from "components/common/EntityDataTable/hooks/useSelectedEntities";
import UserNotification from "util/UserNotification";
import { HoverForHelp, ShareButton, IfPermitted } from "components/common";
import { reportingUrl } from "report/Constants";
import { MoreActions } from "components/common/EntityDataTable";
import ReportRoutes from "report/ReportRoutes";
import useTableFetchContext from "components/common/PaginatedEntityTable/useTableFetchContext";
import EntityShareModal from "components/permissions/EntityShareModal";
import usePluggableEntitySharedActions from "hooks/usePluggableEntitySharedActions";
import useScopePermissions from "hooks/useScopePermissions";
import useSendIlluminateReportDownloadedTelemetry from "report/hooks/useSendIlluminateReportDownloadedTelemetry";
const downloadUrl = (reportId, reportTitle) => new URI(reportingUrl(encodeURI(`${reportId}/generate/${reportTitle}`))).toString();
const _reportHasRecipients = (delivery) => delivery && (Array.isArray(delivery.user_receivers) && delivery.user_receivers.length > 0 || Array.isArray(delivery.email_receivers) && delivery.email_receivers.length > 0);
const SendReportMenuItem = ({
  reportDelivery,
  reportId,
  reportTitle
}) => {
  const { t } = useTranslation("enterprise_report");
  const sendTelemetry = useSendTelemetry("report-form");
  const reportHasRecipients = _reportHasRecipients(reportDelivery);
  const isDisabled = !reportHasRecipients;
  const onSendReport = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.SENT, {
      app_pathname: "report"
    });
    await Reports.sendReport(reportId).then(() => {
      UserNotification.success(t("reports_actions.send_success"), t("reports_actions.send_success_title"));
    }).catch(() => {
      UserNotification.error(
        t("reports_actions.send_error", { title: reportTitle }),
        t("reports_actions.send_error_title")
      );
    });
  };
  return /* @__PURE__ */ React.createElement(MenuItem, { disabled: isDisabled, onSelect: onSendReport }, t("reports_actions.send_now"), isDisabled && /* @__PURE__ */ React.createElement(HoverForHelp, { trigger: ["hover", "click"], placement: "left", displayLeftMargin: true }, t("reports_actions.send_disabled")));
};
const DeleteReportAction = ({ reportId, reportTitle }) => {
  const { t } = useTranslation("enterprise_report");
  const { deselectEntity } = useSelectedEntities();
  const { refetch } = useTableFetchContext();
  const sendTelemetry = useSendTelemetry("reports-overview");
  const onDeleteReport = async () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.DELETED, {
      app_pathname: "report"
    });
    if (window.confirm(t("reports_actions.delete_confirm", { title: reportTitle }))) {
      await Reports.remove(reportId).then(() => {
        deselectEntity(reportId);
        UserNotification.success(
          t("reports_actions.deleted", { title: reportTitle }),
          t("reports_actions.success_title")
        );
        refetch();
      }).catch((error) => {
        UserNotification.error(t("reports_actions.delete_error", { title: reportTitle, error: String(error) }));
      });
    }
  };
  return /* @__PURE__ */ React.createElement(DeleteMenuItem, { onSelect: onDeleteReport });
};
const ReportActions = ({ report }) => {
  const { t } = useTranslation("enterprise_report");
  const [showReportShareModal, setShowReportShareModal] = useState(false);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions(report, "report");
  const moreActions = [pluggableActions.length ? pluggableActions : null].filter(Boolean);
  const { scopePermissions } = useScopePermissions(report);
  const scopeDeletable = scopePermissions.is_deletable !== false;
  const toggleShareModalOpen = useCallback(() => {
    setShowReportShareModal((cur) => !cur);
  }, [setShowReportShareModal]);
  const sendTelemetry = useSendTelemetry("reports-overview");
  const sendIlluminateReportDownloaded = useSendIlluminateReportDownloadedTelemetry("reports-overview");
  const onDownload = () => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.REPORT.DOWNLOAD, {
      app_pathname: "report"
    });
    sendIlluminateReportDownloaded(report);
  };
  return /* @__PURE__ */ React.createElement(ButtonToolbar, null, /* @__PURE__ */ React.createElement(ShareButton, { entityType: "report", entityId: report.id, onClick: toggleShareModalOpen, bsSize: "xs" }), /* @__PURE__ */ React.createElement(MoreActions, null, /* @__PURE__ */ React.createElement(MenuItem, { href: ReportRoutes.contents(report.id) }, t("reports_actions.edit")), /* @__PURE__ */ React.createElement(MenuItem, { href: ReportRoutes.archive(report.id) }, t("reports_actions.archive")), /* @__PURE__ */ React.createElement(MenuItem, { href: downloadUrl(report.id, report.title), target: "_blank", onClick: onDownload }, t("reports_actions.download")), /* @__PURE__ */ React.createElement(SendReportMenuItem, { reportDelivery: report.delivery, reportTitle: report.title, reportId: report.id }), moreActions, scopeDeletable && /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `report:delete:${report.id}` }, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteReportAction, { reportId: report.id, reportTitle: report.title }))), showReportShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: report.id,
      entityType: "report",
      entityTitle: report.title,
      description: t("reports_actions.share_description"),
      onClose: toggleShareModalOpen
    }
  ), pluggableActionModals);
};
export default ReportActions;
